import { useEffect, useMemo, useRef, useState } from 'react';
import { Bookmark, Calculator, CheckCircle2, Clock, Flag, Maximize2, Send, ShieldAlert, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { QuestionRenderer } from '../components/QuestionRenderer';
import { ScientificCalculator } from '../components/ScientificCalculator';
import { VideoPanel } from '../components/VideoPanel';
import { WarningOverlay } from '../components/WarningOverlay';
import { MonitoringStatus } from '../components/MonitoringStatus';
import { calibrationConfig } from '../config/calibration';
import { useExam } from '../context/ExamContext';
import { useAiProctor } from '../hooks/useAiProctor';
import { useAudioMonitor } from '../hooks/useAudioMonitor';
import { useBrowserMonitor } from '../hooks/useBrowserMonitor';
import { useCameraStream } from '../hooks/useCameraStream';
import { useExamMonitoring } from '../hooks/useExamMonitoring';
import { useWebRtcPairing } from '../hooks/useWebRtcPairing';
import { Answer } from '../types/domain';
import { REQUIRED_LONG_ANSWERS, validateExamSubmission } from '../utils/answerValidation';
import { questions, totalDurationMinutes } from '../utils/examData';

const adminPolicy = {
  autoSubmitAfterFinalWarning: false
};

export function MockExamPage() {
  const navigate = useNavigate();
  const { state, startExam, finishExam, setAnswer, setActiveQuestion } = useExam();
  const { stream, error: cameraError } = useCameraStream();
  const phonePairing = useWebRtcPairing();
  const videoRef = useRef<HTMLVideoElement>(null);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const [performanceMode, setPerformanceMode] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(totalDurationMinutes * 60);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [examStarted, setExamStarted] = useState(Boolean(state.startedAt));
  const [submitError, setSubmitError] = useState('');
  const [startError, setStartError] = useState('');
  const activeQuestion = questions.find((question) => question.id === state.activeQuestionId) ?? questions[0];
  const activeIndex = questions.findIndex((question) => question.id === activeQuestion.id);
  const answer = state.answers[activeQuestion.id];

  const browserMonitor = useBrowserMonitor(examStarted, state.startedAt, {
    autoSubmitAfterFinalWarning: adminPolicy.autoSubmitAfterFinalWarning,
    onFinalWarning: () => submitExam(true)
  });
  const ai = useAiProctor(videoRef, examStarted && !performanceMode, state.startedAt);
  useAudioMonitor(stream, state.startedAt, examStarted);
  const monitoring = useExamMonitoring({
    enabled: examStarted,
    laptopVideoRef: videoRef,
    mobileVideoRef,
    laptopStream: stream,
    mobileStream: phonePairing.remoteStream,
    phoneStatus: phonePairing.status,
    phoneCameraSwitching: phonePairing.cameraSwitching,
    mobileRequired: calibrationConfig.publicDemo.mobileCameraRequired
  });

  useEffect(() => {
    if (!examStarted) return undefined;
    const timer = window.setInterval(() => {
      setSecondsLeft((value) => {
        if (value <= 1) {
          window.clearInterval(timer);
          submitExam(true);
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
    // submitExam intentionally stays outside the dependency list so answer edits do not reset the countdown interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examStarted]);

  async function beginExam() {
    setStartError('');
    try {
      const fullscreen = await browserMonitor.requestFullscreen();
      if (!fullscreen) { setStartError('Fullscreen did not start. Allow fullscreen for this site, then retry.'); return; }
      if (!state.startedAt) startExam();
      setExamStarted(true);
    } catch {
      setStartError('Fullscreen was denied. Allow fullscreen for this site, then retry.');
    }
  }

  function updateAnswer(value: Answer['value']) {
    setSubmitError('');
    setAnswer({
      questionId: activeQuestion.id,
      value,
      bookmarked: answer?.bookmarked ?? false,
      reviewLater: answer?.reviewLater ?? false,
      updatedAt: new Date().toISOString()
    });
  }

  function toggleFlag(key: 'bookmarked' | 'reviewLater') {
    setAnswer({
      questionId: activeQuestion.id,
      value: answer?.value ?? '',
      bookmarked: key === 'bookmarked' ? !answer?.bookmarked : Boolean(answer?.bookmarked),
      reviewLater: key === 'reviewLater' ? !answer?.reviewLater : Boolean(answer?.reviewLater),
      updatedAt: new Date().toISOString()
    });
  }

  function submitExam(force = false) {
    if (!force) {
      const validation = validateExamSubmission(questions, state.answers);
      if (!validation.valid) {
        setSubmitError(validation.message);
        return;
      }
    }
    finishExam();
    phonePairing.endSession();
    navigate('/finished');
  }

  const submission = useMemo(() => validateExamSubmission(questions, state.answers), [state.answers]);
  const progress = useMemo(() => Math.round((Object.keys(state.answers).length / questions.length) * 100), [state.answers]);
  const minutes = Math.floor(secondsLeft / 60).toString().padStart(2, '0');
  const seconds = (secondsLeft % 60).toString().padStart(2, '0');

  return (
    <section className="min-h-screen bg-midnight text-slate-50">
      {!examStarted ? <BeginExamOverlay onBegin={beginExam} error={startError} audioStatus={browserMonitor.audioStatus} /> : null}
      <WarningOverlay warnings={[monitoring.latestNotice, browserMonitor.latestWarning]} muted={browserMonitor.muted} onToggleMute={browserMonitor.toggleMute} />
      <header className="sticky top-0 z-30 border-b border-white/10 bg-midnight/88 px-4 py-3 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 font-mono text-lg font-bold"><Clock className="mr-2 inline h-4 w-4 text-neon-blue" />{minutes}:{seconds}</div>
            {browserMonitor.fullscreenRequired ? (
              <Button variant="secondary" icon={<Maximize2 className="h-4 w-4" />} onClick={browserMonitor.requestFullscreen}>Return to Fullscreen</Button>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-300">
            {state.candidate?.mode === 'public-demo' ? <span className="rounded-full border border-neon-blue/40 bg-neon-blue/10 px-3 py-2 font-semibold text-neon-blue">Prototype Demo</span> : null}
            <StatusPill label="Laptop webcam" connected={Boolean(stream)} />
            <StatusPill label="Phone camera" status={phonePairing.status === 'waiting' ? 'Not paired' : phonePairing.status} connected={phonePairing.status === 'connected'} />
            <label className="flex cursor-pointer items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-2">
              <input type="checkbox" className="accent-neon-blue" checked={performanceMode} onChange={(event) => setPerformanceMode(event.target.checked)} />
              Performance Mode
            </label>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1600px] gap-4 px-4 py-4 xl:grid-cols-[300px_minmax(0,1fr)_minmax(360px,0.82fr)]">
        <aside className="glass rounded-2xl p-4">
          <p className="text-sm font-semibold text-slate-300">Progress {progress}%</p>
          <div className="mt-2 h-2 rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-neon-violet to-neon-blue" style={{ width: `${progress}%` }} /></div>
          <div className="mt-5 grid grid-cols-5 gap-2 xl:grid-cols-4">
            {questions.map((question, index) => {
              const saved = state.answers[question.id];
              const active = question.id === activeQuestion.id;
              return (
                <button
                  key={question.id}
                  onClick={() => setActiveQuestion(question.id)}
                  className={`focus-ring h-11 rounded-lg text-sm font-bold transition ${active ? 'bg-white text-midnight' : saved ? 'bg-mint/20 text-mint ring-1 ring-mint/35' : 'bg-white/[0.07] text-slate-300 hover:bg-white/[0.12]'}`}
                  aria-label={`Question ${index + 1}`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
          <div className="mt-5 rounded-xl border border-neon-blue/20 bg-neon-blue/10 p-3 text-sm text-slate-200">
            Long Answers Attempted: {submission.attempted} of {REQUIRED_LONG_ANSWERS} required
          </div>
          <div className="mt-4 grid gap-3">
            <VideoPanel stream={stream} label="Laptop webcam" videoRef={videoRef} />
            <VideoPanel stream={phonePairing.remoteStream} label="Phone camera" videoRef={mobileVideoRef} />
            {phonePairing.status !== 'connected' ? (
              <div className="rounded-xl border border-white/10 bg-white/[0.06] p-3 text-xs text-slate-300">
                <Smartphone className="mb-2 h-4 w-4 text-neon-magenta" />
                Pair the secondary camera from the setup page when required. Waiting for phone connection.
              </div>
            ) : null}
            <MonitoringStatus
              active={monitoring.active}
              presenceCapability={monitoring.presenceCapability}
              presenceState={monitoring.presenceState}
              mobileConnected={phonePairing.status === 'connected'}
              mobileRequired={calibrationConfig.publicDemo.mobileCameraRequired}
            />
          </div>
        </aside>

        <section className="glass rounded-2xl p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold uppercase text-neon-blue">Question {activeIndex + 1} / {questions.length} · {activeQuestion.type.replace('_', ' ')}</p>
              <h1 className="mt-3 text-2xl font-black leading-snug text-white">{activeQuestion.prompt}</h1>
              <p className="mt-2 text-sm text-slate-400">Marks: {activeQuestion.marks} · Negative: {activeQuestion.negativeMarks}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" icon={<Bookmark className="h-4 w-4" />} onClick={() => toggleFlag('bookmarked')}>Bookmark</Button>
              <Button variant="secondary" icon={<Flag className="h-4 w-4" />} onClick={() => toggleFlag('reviewLater')}>Review Later</Button>
            </div>
          </div>
          {activeQuestion.passage ? <div className="mt-5 rounded-xl border border-white/10 bg-black/30 p-4 text-sm leading-7 text-slate-200">{activeQuestion.passage}</div> : null}
          {activeQuestion.supportingMaterial ? <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.06] p-4 text-sm text-slate-300">{activeQuestion.supportingMaterial}</div> : null}
          {activeQuestion.imageUrl ? <img src={activeQuestion.imageUrl} alt="" className="my-5 rounded-xl border border-white/10" /> : null}
        </section>

        <aside className="space-y-4">
          <section className="glass rounded-2xl p-4">
            <button
              type="button"
              onClick={() => setCalculatorOpen((value) => !value)}
              className="focus-ring flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-left font-semibold"
            >
              <span><Calculator className="mr-2 inline h-4 w-4 text-neon-blue" /> Scientific Calculator</span>
              <span>{calculatorOpen ? 'Hide' : 'Open'}</span>
            </button>
            {calculatorOpen ? <div className="mt-3"><ScientificCalculator /></div> : null}
          </section>

          <section className="glass rounded-2xl p-5">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase text-neon-magenta">
              <CheckCircle2 className="h-4 w-4" /> Answer Section
            </div>
            <QuestionRenderer question={activeQuestion} answer={answer} onChange={updateAnswer} />
            {(submitError || cameraError || ai.error) ? (
              <p role="alert" className="mt-4 rounded-xl border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-100">
                {submitError || cameraError || `AI analysis unavailable: ${ai.error}`}
              </p>
            ) : null}
            <Button className="mt-5 w-full" icon={<Send className="h-4 w-4" />} onClick={() => submitExam()}>
              Submit Exam
            </Button>
          </section>
        </aside>
      </main>
    </section>
  );
}

function BeginExamOverlay({ onBegin, error, audioStatus }: { onBegin: () => void; error: string; audioStatus: string }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-midnight/92 p-4 backdrop-blur-xl">
      <div className="glass max-w-xl rounded-2xl p-6 text-center shadow-neon">
        <ShieldAlert className="mx-auto h-12 w-12 text-neon-blue" />
        <h2 className="mt-4 text-3xl font-black">Begin fullscreen exam</h2>
        <p className="mt-3 text-sm leading-6 text-slate-300">
          Fullscreen helps keep the mock examination focused. Browser monitoring is best-effort and cannot provide complete operating-system level lockdown guarantees.
        </p>
        <p className="mt-3 text-sm text-slate-300">Warning audio: <strong>{audioStatus}</strong></p>
        {error ? <p role="alert" className="mt-3 text-sm text-red-300">{error}</p> : null}
        <Button className="mt-6" icon={<Maximize2 className="h-4 w-4" />} onClick={onBegin}>Start Exam</Button>
      </div>
    </div>
  );
}

function StatusPill({ label, connected, status }: { label: string; connected: boolean; status?: string }) {
  return (
    <span className={`rounded-full border px-3 py-2 font-semibold ${connected ? 'border-mint/40 bg-mint/15 text-mint' : 'border-amber-300/35 bg-amber-300/10 text-amber-100'}`}>
      {label}: {status ?? (connected ? 'Connected' : 'Disconnected')}
    </span>
  );
}
